/**
 * What My work lists (PRD-tasks-asks-and-my-work §3, §4.3, FR-3): the things a
 * person has, as one list of rows, each of one kind.
 *
 *   needs-you — the engine's REQUIRED cards (get_responsibilities): the same rows
 *               Home draws, never computed again here
 *   waiting   — the engine's WAITING cards, plus what I asked of others that is
 *               still open: requests I made (any home) and tasks I made for others
 *   promise   — requests made of me that I have not been asked about again
 *               (a promise in force, or one waiting on my answer, when the engine
 *               does not already list it under needs-you)
 *   owned     — items I own, cycles I own, sessions I facilitate, decisions I made
 *               that have a review date
 *   following — what I follow: items, cycles, tracks, sessions, decisions,
 *               commitments (watches, migration 1058)
 *   note      — my own notes not done (a note whose reminder has come is the
 *               engine's, under needs-you)
 *   done      — what ended in the last thirty days: my tasks completed, promises
 *               of mine fulfilled or released, notes I marked done
 *
 * One workspace at a time, like every list. A row belongs to one kind only, so
 * the list read whole has no row twice.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import { useResponsibilityStore } from '@/stores/prioritization/responsibilityStore';
import { getResponsibilityNavigationHref } from '@/lib/responsibilityLinks';
import type { ResponsibilityCard } from '@/types/prioritization/responsibility';
import { listMyNotes, NOTES_CHANGED_EVENT, type PersonalNote } from '@/lib/notesService';
import { REQUESTS_CHANGED_EVENT } from '@/lib/assignmentService';

export type WorkKind = 'needs-you' | 'waiting' | 'promise' | 'owned' | 'following' | 'note' | 'done';

export interface WorkRow {
  id: string;
  kind: WorkKind;
  /** What the row is: Task, Request, Item, Cycle, Session, Decision, Ball… */
  what: string;
  title: string;
  /** One sentence of standing: why it is here, or where it stands. */
  detail: string | null;
  dueAt: string | null;
  /** Sort key: the due date, else far in the future. */
  dueSort: string;
  href: string | null;
  /** The engine's card, when the row is one (its actions are on it). */
  card?: ResponsibilityCard;
  /** The note, when the row is one of my notes. */
  note?: PersonalNote;
  /** What I asked of others that I may ask again (remind_ask, migration 1059). */
  remind?: { type: 'task' | 'assignment'; id: string };
  updatedAt: string | null;
}

const NO_DUE = '9999-12-31';
const DAY = 86_400_000;

const SUBJECT_WORDS: Record<string, string> = {
  task: 'Task',
  session: 'Session',
  proposal: 'Proposal',
  loop: 'Ball',
  ball: 'Ball',
  participation: 'Participation',
  inquiry: 'Question',
  triage_queue: 'Triage',
  v2mom: 'V2MOM',
  v2mom_ask: 'V2MOM',
  assignment_proposed: 'Request',
  assignment_response: 'Request',
  assignment_unanswered: 'Request',
  assignment_due: 'Promise',
  assignment_overdue: 'Promise',
  note: 'Note',
};

function cardRow(card: ResponsibilityCard, kind: WorkKind): WorkRow {
  return {
    id: `card:${card.id}:${card.subject_type}`,
    kind,
    what: SUBJECT_WORDS[card.subject_type] ?? 'Ask',
    title: card.subject,
    detail: card.why ?? null,
    dueAt: card.deadline ?? null,
    dueSort: card.deadline ?? NO_DUE,
    href: getResponsibilityNavigationHref(card),
    card,
    updatedAt: card.created_at ?? null,
  };
}

function assignmentHref(homeType: string | null, homeId: string | null, commitmentId: string | null): string {
  switch (homeType) {
    case 'commitment': return `/prioritization/commitments/${commitmentId ?? homeId}?tab=assignments`;
    case 'item': return `/prioritization/items/${homeId}`;
    case 'cycle': return `/prioritization/cycles/${homeId}`;
    case 'track': return `/prioritization/tracks/${homeId}`;
    case 'session': return `/prioritization/sessions/${homeId}`;
    default: return '/prioritization/dashboard';
  }
}

function sentence(verb: string | null, about: string | null): string {
  const v = verb ? verb.charAt(0).toUpperCase() + verb.slice(1) : 'Do';
  return `${v} ${about ?? 'what was asked'}`;
}

export function useMyWork(userId: string | null | undefined, workspaceId: string | null | undefined) {
  const responsibilities = useResponsibilityStore((s) => s.responsibilities);
  const fetchResponsibilities = useResponsibilityStore((s) => s.fetchResponsibilities);
  const enginedLoading = useResponsibilityStore((s) => s.isLoading);
  const [rows, setRows] = useState<WorkRow[]>([]);
  /** Open tasks I made for others: an engine WAITING card about one of them can be reminded. */
  const [askedTaskIds, setAskedTaskIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    if (userId) void fetchResponsibilities(userId);
    setTick((t) => t + 1);
  }, [userId, fetchResponsibilities]);

  useEffect(() => {
    if (userId) void fetchResponsibilities(userId);
  }, [userId, fetchResponsibilities]);

  // A note or a request made elsewhere on the page shows up here at once.
  useEffect(() => {
    const again = () => refresh();
    window.addEventListener(NOTES_CHANGED_EVENT, again);
    window.addEventListener(REQUESTS_CHANGED_EVENT, again);
    return () => {
      window.removeEventListener(NOTES_CHANGED_EVENT, again);
      window.removeEventListener(REQUESTS_CHANGED_EVENT, again);
    };
  }, [refresh]);

  // The engine's cards, in this workspace.
  const cards = useMemo(
    () => responsibilities.filter((c) => !workspaceId || !c.workspace_id || c.workspace_id === workspaceId),
    [responsibilities, workspaceId],
  );

  useEffect(() => {
    if (!userId || !workspaceId) return;
    let live = true;
    const db = getTypedSupabaseClient();
    const since = new Date(Date.now() - 30 * DAY).toISOString();
    setLoading(true);
    (async () => {
      const [assignments, myTasksDone, othersTasks, items, cycles, sessions, decisions, watches] = await Promise.all([
        db.from('commitment_assignments_v').select('*').eq('workspace_id', workspaceId)
          .or(`assignee_user_id.eq.${userId},authorized_by.eq.${userId}`),
        db.from('prioritization_tasks').select('id, name, status, updated_at, session_id')
          .eq('workspace_id', workspaceId).contains('participant_ids', [userId])
          .eq('status', 'completed').gte('updated_at', since),
        db.from('prioritization_tasks').select('id, name, status, closes_at, updated_at, session_id, participant_ids, category')
          .eq('workspace_id', workspaceId).eq('created_by', userId).in('status', ['draft', 'open'])
          .neq('category', 'catchball_proposal'),
        db.from('items').select('id, title, lifecycle_stage, exit_status, updated_at')
          .eq('workspace_id', workspaceId).eq('steward_id', userId),
        db.from('degap_cycles').select('id, name, status, updated_at').eq('workspace_id', workspaceId).eq('owner_id', userId),
        db.from('prioritization_sessions').select('id, name, state, updated_at, retired_at')
          .eq('workspace_id', workspaceId).eq('facilitator_id', userId).is('retired_at', null)
          .not('state', 'in', '("RUN_COMPLETE","PUBLISHED")'),
        db.from('decisions').select('id, title, status, review_by_date, updated_at')
          .eq('workspace_id', workspaceId).eq('decided_by', userId).not('review_by_date', 'is', null),
        db.from('watches').select('subject_type, subject_id, created_at').eq('workspace_id', workspaceId).eq('user_id', userId),
      ]);
      const failed = [assignments, myTasksDone, othersTasks, items, cycles, sessions, decisions, watches].find((r) => r.error);
      if (failed?.error) throw new Error(failed.error.message);
      const notes = await listMyNotes(userId, workspaceId);

      const out: WorkRow[] = [];
      // Revisions and tasks the engine already lists, so a row is never drawn twice.
      const engineSubjects = new Set(cards.map((c) => c.subject_id));

      for (const a of assignments.data ?? []) {
        const disposition = a.disposition ?? '';
        const about = a.statement ?? null;
        const href = assignmentHref(a.home_type, a.home_id, a.commitment_id);
        const revs = [a.in_force_revision_id, a.proposed_revision_id].filter((x): x is string => !!x);
        if (revs.some((r) => engineSubjects.has(r))) continue;
        if (disposition === 'open' || disposition === 'suspended') {
          const mine = a.assignee_user_id === userId;
          const asked = a.authorized_by === userId;
          if (!mine && !asked) continue;
          const due = a.due_on ?? a.proposed_due_on ?? null;
          out.push({
            id: `assignment:${a.assignment_id}`,
            kind: mine ? 'promise' : 'waiting',
            what: 'Request',
            title: sentence(a.verb, about),
            detail: mine
              ? (a.is_binding ? `You promised this${due ? ` by ${new Date(`${due}T12:00:00Z`).toLocaleDateString()}` : ''}.` : 'Waiting on your answer.')
              : (a.is_binding ? 'They promised it; it is in force.' : 'Waiting on their answer.'),
            dueAt: due,
            dueSort: due ?? NO_DUE,
            href,
            updatedAt: a.authorized_at ?? null,
            ...(mine || !a.assignee_user_id ? {} : { remind: { type: 'assignment' as const, id: a.assignment_id } }),
          });
        } else if ((disposition === 'fulfilled' || disposition === 'released') && a.assignee_user_id === userId) {
          out.push({
            id: `assignment:${a.assignment_id}`,
            kind: 'done',
            what: 'Request',
            title: sentence(a.verb, about),
            detail: disposition === 'fulfilled' ? 'Kept: confirmed done.' : 'Released.',
            dueAt: null,
            dueSort: NO_DUE,
            href,
            updatedAt: a.authorized_at ?? null,
          });
        }
      }

      for (const t of othersTasks.data ?? []) {
        if (engineSubjects.has(t.id)) continue;
        if ((t.participant_ids ?? []).includes(userId)) continue;
        out.push({
          id: `task:${t.id}`,
          kind: 'waiting',
          what: 'Task',
          title: t.name,
          detail: `Asked of ${(t.participant_ids ?? []).length === 1 ? 'one person' : `${(t.participant_ids ?? []).length} people`}; not done yet.`,
          dueAt: t.closes_at ?? null,
          dueSort: t.closes_at ?? NO_DUE,
          href: t.session_id ? `/prioritization/sessions/${t.session_id}` : `/prioritization/task/${t.id}`,
          updatedAt: t.updated_at ?? null,
          remind: { type: 'task', id: t.id },
        });
      }

      for (const t of myTasksDone.data ?? []) {
        out.push({
          id: `task:${t.id}`, kind: 'done', what: 'Task', title: t.name, detail: 'Completed.',
          dueAt: null, dueSort: NO_DUE, href: `/prioritization/task/${t.id}`, updatedAt: t.updated_at ?? null,
        });
      }

      for (const i of items.data ?? []) {
        if (i.exit_status && i.exit_status !== 'active') continue;
        out.push({
          id: `item:${i.id}`, kind: 'owned', what: 'Item', title: i.title ?? '(untitled)',
          detail: i.lifecycle_stage ? `You own it. ${String(i.lifecycle_stage).replace(/_/g, ' ')}.` : 'You own it.',
          dueAt: null, dueSort: NO_DUE, href: `/prioritization/items/${i.id}`, updatedAt: i.updated_at ?? null,
        });
      }
      for (const c of cycles.data ?? []) {
        if (c.status === 'completed' || c.status === 'archived') continue;
        out.push({
          id: `cycle:${c.id}`, kind: 'owned', what: 'Cycle', title: c.name,
          detail: `You own it. ${String(c.status ?? '').replace(/_/g, ' ')}.`,
          dueAt: null, dueSort: NO_DUE, href: `/prioritization/cycles/${c.id}`, updatedAt: c.updated_at ?? null,
        });
      }
      for (const s of sessions.data ?? []) {
        out.push({
          id: `session:${s.id}`, kind: 'owned', what: 'Session', title: s.name,
          detail: 'You facilitate it; it has not run yet.',
          dueAt: null, dueSort: NO_DUE, href: `/prioritization/sessions/${s.id}`, updatedAt: s.updated_at ?? null,
        });
      }
      for (const d of decisions.data ?? []) {
        if (d.status === 'superseded' || d.status === 'reversed') continue;
        out.push({
          id: `decision:${d.id}`, kind: 'owned', what: 'Decision', title: d.title,
          detail: `You made it. Review by ${new Date(`${d.review_by_date}T12:00:00Z`).toLocaleDateString()}.`,
          dueAt: d.review_by_date, dueSort: d.review_by_date ?? NO_DUE,
          href: `/prioritization/decisions/${d.id}`, updatedAt: d.updated_at ?? null,
        });
      }

      // What I follow, by kind, named from its own table.
      const byType = new Map<string, string[]>();
      for (const w of watches.data ?? []) {
        byType.set(w.subject_type, [...(byType.get(w.subject_type) ?? []), w.subject_id]);
      }
      const FOLLOW_KINDS: Record<string, { table: 'items' | 'degap_cycles' | 'tracks' | 'prioritization_sessions' | 'decisions' | 'commitments'; label: 'title' | 'name'; what: string; path: string }> = {
        item: { table: 'items', label: 'title', what: 'Item', path: '/prioritization/items/' },
        cycle: { table: 'degap_cycles', label: 'name', what: 'Cycle', path: '/prioritization/cycles/' },
        track: { table: 'tracks', label: 'name', what: 'Track', path: '/prioritization/tracks/' },
        session: { table: 'prioritization_sessions', label: 'name', what: 'Session', path: '/prioritization/sessions/' },
        decision: { table: 'decisions', label: 'title', what: 'Decision', path: '/prioritization/decisions/' },
        commitment: { table: 'commitments', label: 'title', what: 'Commitment', path: '/prioritization/commitments/' },
      };
      for (const [type, ids] of byType) {
        const k = FOLLOW_KINDS[type];
        if (!k) continue;
        const found = await db.from(k.table).select(`id, ${k.label}, updated_at`).in('id', ids);
        if (found.error) throw new Error(found.error.message);
        for (const r of (found.data ?? []) as unknown as Array<Record<string, string | null>>) {
          out.push({
            id: `following:${type}:${r.id}`, kind: 'following', what: k.what, title: r[k.label] ?? '(untitled)',
            detail: r.updated_at ? `Last changed ${new Date(r.updated_at).toLocaleDateString()}.` : null,
            dueAt: null, dueSort: NO_DUE, href: `${k.path}${r.id}`, updatedAt: r.updated_at ?? null,
          });
        }
      }

      for (const n of notes) {
        if (!n.done_at && engineSubjects.has(n.id)) continue;
        out.push({
          id: `note:${n.id}`,
          kind: n.done_at ? 'done' : 'note',
          what: 'Note',
          title: n.body.length > 120 ? `${n.body.slice(0, 117)}…` : n.body,
          detail: n.done_at
            ? 'Done.'
            : n.remind_at ? `Comes back to you ${new Date(n.remind_at).toLocaleString()}.` : 'No reminder.',
          dueAt: n.done_at ? null : n.remind_at,
          dueSort: (!n.done_at && n.remind_at) || NO_DUE,
          href: n.subject_type && n.subject_id ? (FOLLOW_KINDS[n.subject_type] ? `${FOLLOW_KINDS[n.subject_type].path}${n.subject_id}` : null) : null,
          note: n,
          updatedAt: n.updated_at,
        });
      }

      if (live) {
        setRows(out);
        setAskedTaskIds(new Set((othersTasks.data ?? []).map((t) => t.id)));
        setError(null);
      }
    })()
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [userId, workspaceId, cards, tick]);

  const all = useMemo(() => {
    const fromEngine = cards.flatMap((c) =>
      c.classification === 'REQUIRED' ? [cardRow(c, 'needs-you')]
        : c.classification === 'WAITING'
          ? [{
              ...cardRow(c, 'waiting'),
              ...(c.subject_type === 'task' && askedTaskIds.has(c.subject_id) ? { remind: { type: 'task' as const, id: c.subject_id } } : {}),
            }]
          : []);
    return [...fromEngine, ...rows];
  }, [cards, rows, askedTaskIds]);

  return { rows: all, loading: loading || (enginedLoading && cards.length === 0), error, refresh };
}
